'use client'

import { useState, MouseEvent } from 'react'

import { Accordion, Divider, Icon, Text } from '@vinted/web-ui'
import { SpeechBubble24, Wallet24, Lock24, FastShipper24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { sellerProtectionScreenClickEvent } from '@marketplace-web/seller-protection/seller-protection-data'

const policyIcons = {
  stress_free_shipping: FastShipper24,
  safe_earnings: Wallet24,
  sales_are_final: Lock24,
  quick_issue_resolution: SpeechBubble24,
}

type Props = {
  transactionId: string | undefined | null
  transactionStatus: number | null | undefined
  shipmentStatus: number | null | undefined
}

const SellerProtectionPolicies = ({ transactionId, transactionStatus, shipmentStatus }: Props) => {
  const translate = useTranslate('seller_protection_dialog.policies')
  const { track } = useTracking()
  const [isAccordionExpandedMap, setIsAccordionExpandedMap] = useState<Record<string, boolean>>({
    stress_free_shipping: true,
  })

  const trackPolicyClick = (policyName, isExpanded: boolean) => {
    track(
      sellerProtectionScreenClickEvent({
        target: policyName,
        screen: 'seller_protection_information',
        transactionId,
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
          is_expanded: isExpanded,
        }),
      }),
    )
  }

  const handleToggle = (policyName: string) => (isExpanded: boolean) => {
    trackPolicyClick(policyName, isExpanded)
    setIsAccordionExpandedMap(prev => ({
      ...prev,
      [policyName]: isExpanded,
    }))
  }

  const handleContentClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    track(
      sellerProtectionScreenClickEvent({
        target: 'pack_the_item_well',
        screen: 'seller_protection_information',
        transactionId,
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
        }),
      }),
    )
  }

  const renderPolicyAccordionItem = (policyName: string) => {
    return (
      <>
        <Accordion
          title={translate(`${policyName}.title`)}
          content={
            <span role="none" onClick={handleContentClick}>
              <Text as="p" html text={translate(`${policyName}.body`)} />
            </span>
          }
          headerId={`seller-protection-${policyName}-accordion-header`}
          bodyId={`seller-protection-${policyName}-accordion-body`}
          isExpanded={isAccordionExpandedMap[policyName] || false}
          closedPrefix={<Icon name={policyIcons[policyName]} />}
          expandedPrefix={<Icon name={policyIcons[policyName]} />}
          onToggle={handleToggle(policyName)}
        />
        <Divider key={`divider-${policyName}`} />
      </>
    )
  }

  return (
    <>
      {renderPolicyAccordionItem('stress_free_shipping')}
      {renderPolicyAccordionItem('safe_earnings')}
      {renderPolicyAccordionItem('sales_are_final')}
      {renderPolicyAccordionItem('quick_issue_resolution')}
    </>
  )
}

export default SellerProtectionPolicies
